"use client";

import { useMemo, useState, useCallback } from "react";
import { CopyIcon, CheckIcon, ShareIcon } from "lucide-react";
import { Button } from "@ui/components/button";
import { ShareSummary } from "./ShareSummary";
import {
  marginError,
  mdcPct,
  type ModelId,
} from "../lib/statistics";

interface InterpretationPanelProps {
  prompts: number;
  selectedModels: Set<ModelId>;
  p: number;
  conf: number;
}

export function InterpretationPanel({
  prompts,
  selectedModels,
  p,
  conf,
}: InterpretationPanelProps) {
  const numModels = selectedModels.size;
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const text = useMemo(() => {
    const dailyObs = prompts * 30;
    const threeObs = prompts * 10;
    const weeklyObs = prompts * 5;

    const meDaily = marginError(dailyObs, p, conf).toFixed(1);
    const meThree = marginError(threeObs, p, conf).toFixed(1);
    const meWeekly = marginError(weeklyObs, p, conf).toFixed(1);
    const mdcDaily = mdcPct(dailyObs, p, conf).toFixed(1);
    const confPct = (conf * 100).toFixed(0);
    const pPct = (p * 100).toFixed(0);

    return {
      meDaily,
      meThree,
      meWeekly,
      mdcDaily,
      confPct,
      pPct,
      dailyObs,
      lowBound: (50 - Number.parseFloat(meDaily)).toFixed(1),
      highBound: (50 + Number.parseFloat(meDaily)).toFixed(1),
    };
  }, [prompts, p, conf]);

  const plainText = useMemo(() => {
    const lines = [
      `AI Visibility Confidence Summary`,
      `${prompts} prompts · ${numModels} model${numModels !== 1 ? "s" : ""} · ${text.confPct}% confidence · ${text.pPct}% observed rate`,
      ``,
      `Daily tracking → ${text.dailyObs} obs/mo → ±${text.meDaily}% margin of error. At 50% mention rate, the true value falls between ${text.lowBound}%–${text.highBound}% (${text.confPct}% CI). Min. detectable change: ${text.mdcDaily}%.`,
      ``,
      `Every 3 days → ±${text.meThree}% — solid for monthly trending.`,
      `Weekly → ±${text.meWeekly}% — useful as an early-warning signal only.`,
      ``,
      prompts >= 20
        ? `With ${prompts} prompts, you can break out results by cluster and still report directionally at sub-group level.`
        : `With ${prompts} prompts, be cautious about sub-cluster reporting. Consider reporting clusters in aggregate.`,
      ``,
      `Calculated at spyglasses.io/is-your-ai-visibility-data-reliable`,
    ];
    return lines.join("\n");
  }, [prompts, numModels, text]);

  const handleCopySummary = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback ignored
    }
  }, [plainText]);

  return (
    <div className="rounded-2xl border-l-4 border-l-primary border border-border bg-card p-6 text-sm leading-relaxed text-muted-foreground">
      <div className="flex items-start justify-between gap-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
          Plain-English Summary · {prompts} prompts · {numModels} model
          {numModels !== 1 ? "s" : ""} · {text.confPct}% confidence · {text.pPct}%
          observed rate
        </div>
        <div className="flex shrink-0 gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={handleCopySummary}
          >
            {copied ? (
              <CheckIcon className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <CopyIcon className="h-3.5 w-3.5" />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setShowShare((v) => !v)}
          >
            <ShareIcon className="h-3.5 w-3.5" />
            Share
          </Button>
        </div>
      </div>

      <p>
        At <strong className="text-foreground">{prompts} prompts</strong> per
        brand tracked across{" "}
        <strong className="text-foreground">
          {numModels} model{numModels !== 1 ? "s" : ""}
        </strong>
        , <strong className="text-foreground">daily tracking</strong> gives you{" "}
        {text.dailyObs} monthly observations per model with a margin of error of{" "}
        <strong className="text-foreground">±{text.meDaily}%</strong>. If a brand
        appears in 50% of responses, you can state with {text.confPct}% confidence
        it&apos;s between {text.lowBound}% and {text.highBound}%. You can detect real
        competitive shifts of{" "}
        <strong className="text-foreground">{text.mdcDaily}% or more</strong>.
      </p>

      <p className="mt-4">
        <strong className="text-foreground">Every 3 days</strong> (±{text.meThree}
        %) is solid for monthly trending. You&apos;re building a meaningful picture
        over time even if individual snapshots are less precise.{" "}
        <strong className="text-foreground">Weekly</strong> (±{text.meWeekly}%) is
        best used as an early-warning signal for large changes, not for tight
        competitive comparisons.
      </p>

      <p className="mt-4">
        {prompts >= 20 ? (
          <>
            With <strong className="text-foreground">{prompts} prompts</strong>,
            you have enough coverage to break out results by cluster (persona,
            journey stage, etc.) and still report directionally at the sub-group
            level.
          </>
        ) : (
          <>
            With <strong className="text-foreground">{prompts} prompts</strong>,
            be cautious about reporting at the sub-cluster level. Each segment
            may only have 2-5 prompts, which is too few for reliable standalone
            conclusions. Consider reporting clusters in aggregate.
          </>
        )}
      </p>

      {showShare && (
        <div className="mt-5 border-t pt-5">
          <ShareSummary
            text={plainText}
            pageUrl="https://www.spyglasses.io/is-your-ai-visibility-data-reliable"
          />
        </div>
      )}
    </div>
  );
}
